/**
 * @remarks RuntimeDocumentV1 表达式能力与设计器标识校验
 * @module @yimoka/designable-playground/runtime
 */

import { visitFormilySchemaNodes } from '../shared/schema';

import {
  isObjectRecord,
  pushRuntimeIssue,
  RUNTIME_EXPRESSION_PATTERN,
} from './document-core';
import type {
  IRuntimeDocumentValidationIssue,
} from './document-core';

/** 判断组件名是否是由 Formily 在运行时求值的表达式。 */
export const isRuntimeSchemaExpression = (value: string) => (
  /^\s*\{\{[\s\S]*\}\}\s*$/.test(value)
);

/** 递归拒绝动态或带首尾空白的组件名，Runtime v1 只允许可静态审计的注册组件。 */
export const validateRuntimeStaticComponentNames = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  visitFormilySchemaNodes(value, path, ({ node, path: nodePath }) => {
    (['x-component', 'x-decorator'] as const).forEach((propertyName) => {
      const componentName = node[propertyName];
      if (
        typeof componentName === 'string'
        && isRuntimeSchemaExpression(componentName)
      ) {
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          `${nodePath}.${propertyName}`,
          `${propertyName} 不支持动态表达式，Runtime v1 只允许静态注册组件名`,
        );
      } else if (
        typeof componentName === 'string'
        && componentName.trim() !== componentName
      ) {
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          `${nodePath}.${propertyName}`,
          `${propertyName} 静态组件名不能包含首尾空白`,
        );
      }
    });
  });
};

export const RUNTIME_EXPRESSION_FORBIDDEN_SYNTAX = /(?:=>|`|[;{}()]|\+\+|--|\b(?:await|class|delete|function|import|new|yield)\b|(?:^|[^=!<>])=(?:[^=]|$))/;
export const RUNTIME_EXPRESSION_ALLOWED_PUNCTUATION = new Set('.[]?!<>=&|:+-*/%,');
export const RUNTIME_AUTO_NETWORK_PROP_KEYS = new Set([
  'fallback',
  'poster',
  'src',
  'srcSet',
  'thumbUrl',
]);
export const RUNTIME_COMPONENT_AUTO_NETWORK_PROP_KEYS: Record<string, ReadonlySet<string>> = {
  Avatar: new Set(['value']),
  Empty: new Set(['image']),
  Image: new Set(['image', 'value']),
  QRCode: new Set(['icon']),
  Upload: new Set(['url']),
};
export const RUNTIME_FIELD_VALUE_AUTO_NETWORK_COMPONENTS = new Set([
  'Avatar',
  'Image',
]);
export const RUNTIME_REACTION_FIELD_VALUE_KEYS = new Set([
  'initialValue',
  'value',
]);
// 同源组件别名共用同一份可调用路径，避免上游 antd 能力扩展后只修复其中一个入口。
export const RUNTIME_FORM_CALLABLE_PATHS = new Set(['feedbackIcons', 'requiredMark']);
export const RUNTIME_INPUT_COUNT_CALLABLE_PATHS = new Set([
  'count.exceedFormatter',
  'count.show',
  'count.strategy',
]);
export const RUNTIME_TABS_INDICATOR_CALLABLE_PATHS = new Set(['indicator.size']);
export const RUNTIME_CARD_TABS_CALLABLE_PATHS = new Set(['tabProps.indicator.size']);
export const RUNTIME_SAFE_NATIVE_COMPONENT_TAGS = new Set([
  'article',
  'aside',
  'div',
  'footer',
  'header',
  'main',
  'nav',
  'section',
  'span',
]);
export const RUNTIME_COMPONENT_CALLABLE_EXPRESSION_PROP_PATHS: Record<string, ReadonlySet<string>> = {
  Card: RUNTIME_CARD_TABS_CALLABLE_PATHS,
  Cascader: new Set(['showSearch.sort']),
  ColorPicker: new Set(['showText']),
  DatePicker: new Set(['components', 'format']),
  DateRangePicker: new Set(['components', 'format']),
  Form: RUNTIME_FORM_CALLABLE_PATHS,
  FormLayout: RUNTIME_FORM_CALLABLE_PATHS,
  Image: new Set(['preview.getContainer']),
  Input: RUNTIME_INPUT_COUNT_CALLABLE_PATHS,
  InputSearch: RUNTIME_INPUT_COUNT_CALLABLE_PATHS,
  I18nEditor: new Set(['InContent', 'OutContent']),
  Object: RUNTIME_CARD_TABS_CALLABLE_PATHS,
  Password: RUNTIME_INPUT_COUNT_CALLABLE_PATHS,
  QRCode: new Set(['statusRender']),
  Rate: new Set(['character']),
  Select: new Set(['menuItemSelectedIcon']),
  Tabs: RUNTIME_TABS_INDICATOR_CALLABLE_PATHS,
  FormTab: RUNTIME_TABS_INDICATOR_CALLABLE_PATHS,
  Text: new Set(['copyable.text']),
  TextArea: RUNTIME_INPUT_COUNT_CALLABLE_PATHS,
  TimePicker: new Set(['components', 'format']),
  TimeRangePicker: new Set(['components', 'format']),
  Transfer: new Set(['children', 'footer', 'listStyle', 'selectAllLabels']),
  TreeSelect: new Set(['switcherIcon']),
  Upload: new Set([
    'showUploadList.downloadIcon',
    'showUploadList.extra',
    'showUploadList.previewIcon',
    'showUploadList.removeIcon',
    'showUploadList.showDownloadIcon',
    'showUploadList.showPreviewIcon',
    'showUploadList.showRemoveIcon',
  ]),
};
export const RUNTIME_COMPONENT_CALLABLE_EXPRESSION_PROP_KEY_PATHS: Record<
  string,
  Record<string, ReadonlySet<string>>
> = {
  DatePicker: { presets: new Set(['value']) },
  DateRangePicker: { presets: new Set(['value']) },
  TimePicker: { presets: new Set(['value']) },
  TimeRangePicker: { presets: new Set(['value']) },
};
export const RUNTIME_DECORATOR_CALLABLE_EXPRESSION_PROP_PATHS: Record<string, ReadonlySet<string>> = {
  FormItem: new Set([
    'getValueFromEvent',
    'getValueProps',
    'normalize',
    'shouldUpdate',
    'tooltip',
  ]),
};
export const RUNTIME_DECORATOR_CALLABLE_EXPRESSION_PROP_KEY_PATHS: Record<
  string,
  Record<string, ReadonlySet<string>>
> = {
  FormItem: { rules: new Set(['transform', 'validator']) },
};
export const RUNTIME_SCHEMA_CALLABLE_EXPRESSION_KEYS = new Set(['validator']);
export const RUNTIME_CALLABLE_EXPRESSION_PROP_KEYS = new Set([
  'afterClose',
  'beforeUpload',
  'cellRender',
  'classNames',
  'component',
  'customRequest',
  'disabledDate',
  'disabledHours',
  'disabledMinutes',
  'disabledSeconds',
  'disabledTime',
  'displayRender',
  'dropdownRender',
  'expandIcon',
  'filter',
  'filterOption',
  'filterSort',
  'filterTreeNode',
  'formatter',
  'getCalendarContainer',
  'getPopupContainer',
  'iconRender',
  'imageRender',
  'isImageUrl',
  'itemRender',
  'labelRender',
  'loadData',
  'maxTagPlaceholder',
  'optionRender',
  'panelRender',
  'parser',
  'popupRender',
  'previewFile',
  'ref',
  'render',
  'renderExtraFooter',
  'renderTabBar',
  'rowKey',
  'tagRender',
  'toolbarRender',
  'treeTitleRender',
  'validateSearch',
]);
export const RUNTIME_STYLE_PROP_TREE_KEYS = new Set(['style', 'styles']);
export const RUNTIME_DATA_KEY_FORBIDDEN_SEGMENTS = new Set([
  '__proto__',
  'constructor',
  'prototype',
]);

/** Runtime dataKey 只允许读取 Store 的纯数据入口，不能遍历实例方法或原型链。 */
export const isAllowedRuntimeDataKeyPath = (dataKey: string) => {
  if (!dataKey) return true;
  const segments = dataKey.split('.');
  if (segments.some(segment => (
    !/^[\p{L}\p{N}_$-]+$/u.test(segment)
    || RUNTIME_DATA_KEY_FORBIDDEN_SEGMENTS.has(segment)
  ))) return false;
  const [root, second] = segments;
  return (
    (['dict', 'extInfo', 'values'].includes(root) && segments.length >= 2)
    || root === 'listData'
    || (root === 'response' && second === 'data')
  );
};

/** 判断表达式字符是否属于数据读取语法的正向字符集。 */
export const hasOnlyRuntimeDataExpressionCharacters = (expression: string) => (
  [...expression].every(character => (
    /[\s\p{L}\p{N}_$]/u.test(character)
    || RUNTIME_EXPRESSION_ALLOWED_PUNCTUATION.has(character)
  ))
);

/** 用空白替换字符串字面量，后续只审计实际可执行语法。 */
export const maskRuntimeExpressionStrings = (expression: string): string | undefined => {
  let quote: string | undefined;
  let escaped = false;
  let masked = '';
  for (const character of expression) {
    if (quote) {
      masked += ' ';
      if (escaped) {
        escaped = false;
      } else if (character === '\\') {
        escaped = true;
      } else if (character === quote) {
        quote = undefined;
      }
      continue;
    }
    if (character === '"' || character === '\'') {
      quote = character;
      masked += ' ';
      continue;
    }
    masked += character;
  }
  return quote ? undefined : masked;
};

/** Runtime v1 表达式只允许无调用、无赋值的声明式数据读取。 */
export const isAllowedRuntimeDataExpression = (value: string) => {
  const match = RUNTIME_EXPRESSION_PATTERN.exec(value);
  if (!match) return true;
  const masked = maskRuntimeExpressionStrings(match[1]);
  return Boolean(
    masked
    && !RUNTIME_EXPRESSION_FORBIDDEN_SYNTAX.test(masked)
    && hasOnlyRuntimeDataExpressionCharacters(masked),
  );
};

/** 按点路径读取属性，专属嵌套函数槽位不能退化成普通数据表达式。 */
export const getRuntimePropPathValue = (
  value: Record<string, unknown>,
  propertyPath: string,
) => propertyPath.split('.').reduce<unknown>((current, segment) => (
  isObjectRecord(current) ? current[segment] : undefined
), value);

/** 收集属性值及数组元素中的全部 Runtime 表达式路径。 */
export const findRuntimeSchemaExpressionPaths = (value: unknown, path: string): string[] => {
  if (typeof value === 'string') {
    return isRuntimeSchemaExpression(value) ? [path] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => (
      findRuntimeSchemaExpressionPaths(item, `${path}[${index}]`)
    ));
  }
  if (!isObjectRecord(value)) return [];
  return Object.entries(value).flatMap(([key, child]) => (
    findRuntimeSchemaExpressionPaths(child, `${path}.${key}`)
  ));
};

/** 收集值或数组元素直接承载的表达式，不误伤 reactions 对象里的声明式状态表达式。 */
export const findRuntimeDirectExpressionPaths = (value: unknown, path: string): string[] => {
  if (typeof value === 'string') {
    return isRuntimeSchemaExpression(value) ? [path] : [];
  }
  if (!Array.isArray(value)) return [];
  return value.flatMap((item, index) => (
    findRuntimeDirectExpressionPaths(item, `${path}[${index}]`)
  ));
};

/** 收集对象树里指定键承载的非空脚本文本，兼容 reactions 的对象与数组形态。 */
export const findRuntimeNonEmptyStringKeyPaths = (
  value: unknown,
  path: string,
  stringKeys: ReadonlySet<string>,
): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => (
      findRuntimeNonEmptyStringKeyPaths(item, `${path}[${index}]`, stringKeys)
    ));
  }
  if (!isObjectRecord(value)) return [];
  return Object.entries(value).flatMap(([key, child]) => {
    const childPath = `${path}.${key}`;
    if (stringKeys.has(key) && typeof child === 'string' && child.trim()) {
      return [childPath];
    }
    return findRuntimeNonEmptyStringKeyPaths(child, childPath, stringKeys);
  });
};

/** 收集对象树里指定函数键承载的表达式，同时保留数组索引供宿主精确定位。 */
export const findRuntimeCallableKeyExpressionPaths = (
  value: unknown,
  path: string,
  callableKeys: ReadonlySet<string>,
): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => (
      findRuntimeCallableKeyExpressionPaths(item, `${path}[${index}]`, callableKeys)
    ));
  }
  if (!isObjectRecord(value)) return [];
  return Object.entries(value).flatMap(([key, child]) => {
    const childPath = `${path}.${key}`;
    return callableKeys.has(key)
      ? findRuntimeSchemaExpressionPaths(child, childPath)
      : findRuntimeCallableKeyExpressionPaths(child, childPath, callableKeys);
  });
};

/** 审计一组静态属性路径，统一阻断组件和装饰器会自动调用的表达式值。 */
export const validateRuntimeCallablePropPaths = (
  value: Record<string, unknown>,
  propertyPaths: ReadonlySet<string> | undefined,
  path: string,
  issues: IRuntimeDocumentValidationIssue[],
) => {
  propertyPaths?.forEach((propertyPath) => {
    const propertyValue = getRuntimePropPathValue(value, propertyPath);
    findRuntimeSchemaExpressionPaths(
      propertyValue,
      `${path}.${propertyPath}`,
    ).forEach((expressionPath) => {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        expressionPath,
        'Runtime v1 表达式只允许无调用、无赋值的数据读取，事件能力必须由受信组件提供',
      );
    });
  });
};

/** 审计属性子树里的函数键，兼容 rules、presets 等数组配置。 */
export const validateRuntimeCallablePropKeyPaths = (
  value: Record<string, unknown>,
  propertyKeyPaths: Record<string, ReadonlySet<string>> | undefined,
  path: string,
  issues: IRuntimeDocumentValidationIssue[],
) => {
  Object.entries(propertyKeyPaths ?? {}).forEach(([propertyPath, callableKeys]) => {
    const propertyValue = getRuntimePropPathValue(value, propertyPath);
    findRuntimeCallableKeyExpressionPaths(
      propertyValue,
      `${path}.${propertyPath}`,
      callableKeys,
    ).forEach((expressionPath) => {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        expressionPath,
        'Runtime v1 表达式只允许无调用、无赋值的数据读取，事件能力必须由受信组件提供',
      );
    });
  });
};

/** 递归校验所有 Runtime 表达式只能读取数据，事件回调必须由受信组件实现。 */
export const validateRuntimeExpressionCapabilities = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
  key = '',
) => {
  if (/^on[A-Z]/.test(key) && value !== undefined) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      path,
      'Runtime 文档不能声明事件属性，事件能力必须由受信组件提供',
    );
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      validateRuntimeExpressionCapabilities(item, issues, `${path}[${index}]`);
    });
    return;
  }
  if (typeof value === 'string' && isRuntimeSchemaExpression(value)) {
    if (
      !isAllowedRuntimeDataExpression(value)
      || RUNTIME_CALLABLE_EXPRESSION_PROP_KEYS.has(key)
    ) {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        path,
        'Runtime v1 表达式只允许无调用、无赋值的数据读取，事件能力必须由受信组件提供',
      );
    }
    return;
  }
  if (!isObjectRecord(value)) return;
  Object.entries(value).forEach(([childKey, child]) => {
    validateRuntimeExpressionCapabilities(child, issues, `${path}.${childKey}`, childKey);
  });
};

/** 校验真正进入 Designable TreeNode 的 Schema 节点 ID 类型与全局唯一性。 */
export const validateRuntimeDesignableIds = (
  value: Record<string, unknown>,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  const seenIds = new Set<string>();

  /** 递归 properties/items，与 Formily Transformer 创建 TreeNode 的边界保持一致。 */
  const visitConvertedNode = (node: Record<string, unknown>, nodePath: string) => {
    const { 'x-designable-id': id } = node;
    if (id !== undefined) {
      if (typeof id !== 'string' || !id.trim() || id !== id.trim()) {
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          `${nodePath}.x-designable-id`,
          'x-designable-id 必须是无首尾空白的非空字符串',
        );
      } else if (seenIds.has(id)) {
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          `${nodePath}.x-designable-id`,
          `x-designable-id ${id} 重复`,
        );
      } else {
        seenIds.add(id);
      }
    }
    const { properties } = node;
    if (isObjectRecord(properties)) {
      Object.entries(properties).forEach(([key, child]) => {
        if (isObjectRecord(child)) {
          visitConvertedNode(child, `${nodePath}.properties.${key}`);
        }
      });
    }
    const { items } = node;
    if (Array.isArray(items)) {
      items.forEach((child, index) => {
        if (isObjectRecord(child)) {
          visitConvertedNode(child, `${nodePath}.items[${index}]`);
        }
      });
    } else if (isObjectRecord(items)) {
      visitConvertedNode(items, `${nodePath}.items`);
    }
  };

  const { properties } = value;
  if (isObjectRecord(properties)) {
    Object.entries(properties).forEach(([key, child]) => {
      if (isObjectRecord(child)) {
        visitConvertedNode(child, `${path}.properties.${key}`);
      }
    });
  }
};
